import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useTheme,
} from "@mui/material";
import { prettyPrintEnumValue } from "encamp-shared/src/utils/prettyPrintEnumValue";
import { formatCurrency } from "util/currency";
import { prettyPrintDateTime } from "util/dates";

/**
 * Structural row shape (rather than a query-derived type) so any query that
 * selects the payment display fields can feed this component — the read-only
 * submission view's full query and the stepper's slimmer step query alike.
 */
type SubmissionPaymentRow = {
  id: string;
  amount?: number | null;
  payment: {
    id: string;
    amount?: number | null;
    payee?: string | null;
    paidAt?: string | null;
    status?: string | null;
  };
};

interface SubmissionPaymentDisplayProps {
  submissionPayments: SubmissionPaymentRow[];
}

export function SubmissionPaymentDisplay({ submissionPayments }: SubmissionPaymentDisplayProps) {
  const theme = useTheme();

  if (!submissionPayments || submissionPayments.length === 0) {
    return (
      <Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Payments
        </Typography>
        <Typography variant="body1" color="text.secondary">
          No payments
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        Payments
      </Typography>
      <TableContainer
        component={Paper}
        variant="outlined"
        sx={{
          border: `1px solid ${theme.palette.divider}`,
          "& .MuiTableCell-root": {
            borderColor: theme.palette.divider,
          },
        }}
      >
        <Table size="small">
          <TableHead>
            <TableRow
              sx={{
                backgroundColor: theme.palette.grey50.main,
              }}
            >
              <TableCell sx={{ fontWeight: 500 }}>Status</TableCell>
              <TableCell sx={{ fontWeight: 500 }}>Payee</TableCell>
              <TableCell sx={{ fontWeight: 500 }}>Payment Date</TableCell>
              <TableCell sx={{ fontWeight: 500 }} align="right">
                Amount
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {submissionPayments.map((submissionPayment) => {
              const displayAmount = submissionPayment.amount ?? submissionPayment.payment.amount;

              return (
                <TableRow key={submissionPayment.id}>
                  <TableCell>
                    {submissionPayment.payment.status ? (
                      <Typography variant="body2" color="text.primary">
                        {prettyPrintEnumValue(submissionPayment.payment.status)}
                      </Typography>
                    ) : (
                      <Typography variant="body2" color="text.disabled">
                        —
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>{submissionPayment.payment.payee || "—"}</TableCell>
                  <TableCell>
                    {submissionPayment.payment.paidAt
                      ? prettyPrintDateTime(submissionPayment.payment.paidAt)
                      : "—"}
                  </TableCell>
                  <TableCell align="right">{formatCurrency(displayAmount)}</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}
